﻿import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Pencil, Trash, FileSpreadsheet, Download, Upload } from "lucide-react";
import { toast } from "react-toastify";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import EditableSelectCell from "@/components/desktop/list/table/cells/SelectCell/EditableSelectCell";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomInfoMessage from "@/components/desktop/list/CustomInfoMessage";
import { CustomOption, CustomSingleValue } from "@/components/desktop/list/table/cells/SelectCell/CustomSelectComponents";

import { getToday } from "@/services/Utils";
import { transformCropsWithSagIcon } from "@/utils/cropOptions.utils";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";

import useListController from "@/hooks/list/useListController";
import {
	useSpecieVarietyLookups,
	useSpecieVarieties,
	useUpdateSpecieVarietyLookups,
	useSaveSpecieVariety,
	useDeleteSpecieVariety,
	useImportExcelSpecieVariety,
} from "@/hooks/maintainers/BusinessCenter/SpecieVariety/useSpecieVariety";

import { base_filter, base_table } from "./specie_variety.constants";

import SpecieVarietyForm from "./Form/SpecieVarietyForm";

const SpecieVariety = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const inputRefExcel = useRef(null);

	const { user } = useSelector((s) => s.auth);
	const { estate_id, estate_parent_id } = useSelector((s) => s.auth.filter);

	const { tableState, appliedFilterParams, reactTableSort, handlePageChange, handlePageSizeChange, handleSortByChange } = useListController({
		baseFilter: base_filter,
		baseTable: base_table,
	});

	const { data: lookups, isLoading: isLoadingLookups } = useSpecieVarietyLookups();
	const { addCropToLookups } = useUpdateSpecieVarietyLookups();

	const { data, isFetching, isLoading } = useSpecieVarieties({
		estate_id,
		estate_parent_id,
		...appliedFilterParams,
		...tableState,
	});

	const { mutate: deleteSpecieVariety, isLoading: isLoadingDelete } = useDeleteSpecieVariety();
	const { mutate: saveSpecieVariety, isLoading: isLoadingSave } = useSaveSpecieVariety();
	const { mutate: importExcelSpecieVariety, isLoading: isLoadingImport } = useImportExcelSpecieVariety();

	const [showDelete, setShowDelete] = useState(false);
	const [selected, setSelected] = useState(null);
	const [infoMessages, setInfoMessages] = useState([]);
	const [messageType, setMessageType] = useState("info");

	const crops = lookups?.crops;
	const estates_parents = lookups?.estates_parents;

	const cropsOptions = useMemo(() => {
		return transformCropsWithSagIcon(crops);
	}, [crops]);

	const estatesParentsOptions = useMemo(() => {
		if (!estates_parents) return [];
		return estates_parents;
	}, [estates_parents]);

	const handleExportExcel = () => {
		const fileName = `${getToday()} Excel Especies y Variedades ${user?.customer?.name}.xlsx`;
		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: {
						estate_id,
						estate_parent_id,
						...appliedFilterParams,
						...tableState,
					},
					show: true,
					url: "/maintainers/business_center/specie_variety/export_excel",
					name: fileName,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	const handleImportExcel = () => {
		if (inputRefExcel.current) {
			inputRefExcel.current.click();
		}
	};

	const handleFileChange = (event) => {
		const file = event.target.files[0];

		if (!file) {
			return;
		}

		const formData = new FormData();
		formData.append("file", file);

		importExcelSpecieVariety(formData, {
			onSuccess: (response) => {
				inputRefExcel.current.value = null;
				if (response?.message) {
					toast.success(response.message);
				} else {
					toast.success("Excel importado correctamente");
				}
			},
			onError: (error) => {
				inputRefExcel.current.value = null;
				toast.error(error?.response?.data?.message || "Error al importar Excel");
			},
		});
	};

	const handleDelete = () => {
		setShowDelete(false);

		deleteSpecieVariety(
			{
				selected,
				filters: {
					estate_id,
					estate_parent_id,
					...appliedFilterParams,
					...tableState,
				},
			},
			{
				onSuccess: () => {
					toast.success("Especie/Variedad eliminada correctamente");
				},
				onError: () => {
					toast.error("Error al eliminar especie/variedad");
				},
			},
		);
	};

	const handleSave = (key, params) => {
		setSelected(params.recordId);

		const specieObject = key === "specie" ? params.selectedValue : params.originalRow.specie;

		const varietiesObjects = key === "varieties" ? params.selectedValue : params.originalRow.varieties;

		const estatesParentsObjects = key === "estates_parents" ? params.selectedValue : params.originalRow.estates_parents;

		const formData = {
			id: params.recordId,
			specie: key === "specie" ? params.newValue : params.originalRow.specie?.value || params.originalRow.specie,
			varieties: key === "varieties" ? params.newValue : (params.originalRow.varieties || []).map((v) => v.value || v),
			estates_parents: key === "estates_parents" ? params.newValue : (params.originalRow.estates_parents || []).map((e) => e.value || e),
			specie_object: specieObject,
			varieties_objects: varietiesObjects,
			estates_parents_objects: estatesParentsObjects,
		};

		const payload = {
			form: formData,
			filters: {
				estate_id,
				estate_parent_id,
				...appliedFilterParams,
				...tableState,
			},
		};

		saveSpecieVariety(payload, {
			onSuccess: (response) => {
				setSelected(null);
				toast.success("Especie/Variedad guardada correctamente");

				if (response?.info_messages && response.info_messages.length > 0) {
					setInfoMessages(response.info_messages);
					setMessageType("info");
				}
			},
			onError: () => {
				setSelected(null);
				toast.error("Error al guardar Especie/Variedad");
			},
		});
	};

	const columns = useMemo(
		() => [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: 20,
				Cell: ({ row }) => {
					return <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>;
				},
			},
			{
				Header: "",
				accessor: "from_sag",
				width: 20,
				disableSortBy: true,
				Cell: ({ value }) =>
					value ? (
						<img src="/sag.jpg" alt="SAG" style={{ width: "30px", height: "auto", display: "block" }} title="Producto registrado en SAG" />
					) : null,
			},
			{
				Header: "Especie",
				accessor: "specie",
				width: 100,
				disableSortBy: true,
				Cell: ({ row, value }) => {
					return (
						<EditableSelectCell
							value={value}
							row={row}
							fieldName="specie"
							options={cropsOptions}
							onSave={(params) => handleSave("specie", params)}
							placeholder="Seleccione especie..."
							isCreatable={true}
							onCreateOption={(inputValue, newOption) => {
								addCropToLookups(newOption);
							}}
							formatCreateLabel={(inputValue) => `Crear especie "${inputValue}"`}
							newOptionDefaults={{
								from_sag: false,
							}}
							components={{
								Option: CustomOption,
								SingleValue: CustomSingleValue,
							}}
						/>
					);
				},
			},
			{
				Header: "Variedad",
				accessor: "varieties",
				disableSortBy: true,
				width: 200,
				Cell: ({ row, value }) => {
					return (
						<EditableSelectCell
							value={value}
							row={row}
							fieldName="varieties"
							options={value || []}
							onSave={(params) => handleSave("varieties", params)}
							isMulti={true}
							placeholder="Seleccione variedades..."
							isCreatable={true}
							formatCreateLabel={(inputValue) => `Crear variedad "${inputValue}"`}
							newOptionDefaults={{}}
						/>
					);
				},
			},
			{
				Header: "Acción",
				disableSortBy: true,
				accessor: "id",
				width: 50,
				Cell: ({ value }) => {
					return isLoadingSave && selected === value ? (
						<CustomFetching size={20} />
					) : (
						<CustomDropdown
							title="Acciones"
							items={[
								{
									label: "Editar",
									action: () => navigate("/configuracion/especies_variedades/editar/" + value),
									icon: Pencil,
								},
								{
									label: "Eliminar",
									action: () => {
										setSelected(value);
										setShowDelete(true);
									},
									icon: Trash,
									danger: true,
								},
							]}
							align="right"
						/>
					);
				},
			},
		],
		// eslint-disable-next-line
		[history, cropsOptions, estatesParentsOptions, isLoadingSave],
	);

	return (
		<ListScaffold
			titleBreadcrumbs={[
				{ label: "Dashboard", onClick: () => navigate("/") },
				{ label: "Configuración", onClick: () => navigate("/configuracion") },
				{ label: "Especies y Variedades", onClick: () => navigate("/configuracion/especies_variedades") },
			]}
			subtitle="Gestione las especies y variedades asociadas a sus centro de costos."
			showCreate
			createLabel="Nueva especie o variedad"
			onCreate={() => navigate("/configuracion/especies_variedades/crear")}
			actionButtons={[
				{
					key: "actions",
					label: "Acciones",
					icon: FileSpreadsheet,
					options: [
						{
							key: "export",
							label: "Exportar Excel",
							icon: Download,
							onClick: handleExportExcel,
						},
						{
							key: "import",
							label: "Importar Excel",
							icon: Upload,
							onClick: handleImportExcel,
						},
					],
				},
			]}
			filterTitle="Filtrar especies y variedades"
			isFetching={isFetching}
			isLoading={isLoading || isLoadingDelete || isLoadingLookups || isLoadingImport}
			data={data?.data || []}
			totalItems={data?.total || 0}
			totalPages={data?.last_page || 0}
			toItem={data?.to || 0}
			fromItem={data?.from || 0}
			pageIndex={tableState.page}
			columns={columns}
			onPageChange={handlePageChange}
			pageSize={tableState.per_page}
			onPageSizeChange={handlePageSizeChange}
			sortBy={reactTableSort}
			onSortByChange={handleSortByChange}
			pageSizeOptions={[20, 50, 100]}
			initialPageSize={tableState.per_page}
			initialSortBy={reactTableSort}
			topContent={
				infoMessages.length > 0 && (
					<CustomInfoMessage messages={infoMessages} type={messageType} autoCloseDuration={30000} onClose={() => setInfoMessages([])} />
				)
			}
		>
			<input ref={inputRefExcel} type="file" accept=".xlsx, .xls" style={{ display: "none" }} onChange={handleFileChange} />

			<SpecieVarietyForm
				filters={{
					estate_id,
					estate_parent_id,
					...appliedFilterParams,
					...tableState,
				}}
			/>

			<BasicConfirm
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation={"eliminar especie/variedad"}
				subMessage="Esta acción dejará todos los centros de costos relacionados sin especie o variedad asociada."
			/>
		</ListScaffold>
	);
};

export default SpecieVariety;
